import React, { useState, useEffect } from 'react';
import { collectionService } from '../../services/collectionService';
import './CollectionBoard.css';

const CollectionBoard = ({ 
    channel, 
    collectionSetId, 
    isOwner = false, 
    isReadOnly = false 
}) => {
    const [collections, setCollections] = useState([]);
    const [setItems, setSetItems] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [collectionSearch, setCollectionSearch] = useState('');
    const [isCollectionSearchOpen, setIsCollectionSearchOpen] = useState(false);

    // ✅ Состояния загрузки
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const channelId = channel?.id;
    const canEdit = isOwner && !isReadOnly;

    // ============================================================
    // Загрузка коллекций (с развилкой: публичный / защищённый)
    // ============================================================
    useEffect(() => {
        if (!channelId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const fetchCollections = isReadOnly
            ? collectionService.getPublicCollections(channelId)
            : collectionService.getCollectionsByChannelId(channelId);

        fetchCollections
            .then(data => {
                setCollections(data || []);
                setLoading(false);
            })
            .catch(err => {
                console.error('Ошибка загрузки коллекций:', err);
                setError('Не удалось загрузить коллекцию');
                setLoading(false);
            });
    }, [channelId, isReadOnly]);

    // ============================================================
    // Загрузка элементов набора (только для редактирования)
    // ============================================================
    useEffect(() => {
        if (!collectionSetId || !canEdit) return;

        const fetchItems = isReadOnly
            ? collectionService.getPublicSetItems(collectionSetId)
            : collectionService.getCollectionSetItems(collectionSetId);

        fetchItems
            .then(data => {
                const existingTitles = collections.map(c => c.title);
                const filtered = data.filter(item => !existingTitles.includes(item.title));
                setSetItems(filtered);
            })
            .catch(err => console.error('Ошибка загрузки элементов набора:', err));
    }, [collectionSetId, collections, canEdit, isReadOnly]);

    // ============================================================
    // Обработчики (только для canEdit)
    // ============================================================
    const toggleItemSelection = (itemId) => {
        setSelectedItemIds(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const handleAddSelected = () => {
        if (selectedItemIds.length === 0) return;

        const selectedItems = setItems.filter(item => selectedItemIds.includes(item.id));

        const newCollections = selectedItems.map(item => ({
            title: item.title,
            description: item.description,
            imgUrl: item.imgUrl,
            tags: item.tags || [],
            channelId: channelId,
            collectionSetId: collectionSetId
        }));

        Promise.all(
            newCollections.map(col => collectionService.createCollection(channelId, col))
        )
        .then(createdItems => {
            setCollections([...collections, ...createdItems]);
            setShowModal(false);
            setSelectedItemIds([]);
        })
        .catch(err => console.error('Ошибка добавления элементов:', err));
    };

    const handleDelete = (id) => {
        if (!canEdit) return;
        if (!window.confirm('Удалить этот элемент из коллекции?')) return;

        collectionService.deleteCollection(id)
            .then(() => setCollections(collections.filter(c => c.id !== id)))
            .catch(err => console.error('Ошибка удаления:', err));
    };

    // ============================================================
    // Фильтрация
    // ============================================================
    const filteredItems = setItems.filter(item => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase().trim();
        return (
            item.title?.toLowerCase().includes(q) ||
            item.description?.toLowerCase().includes(q) ||
            item.tags?.some(tag => tag.toLowerCase().includes(q))
        );
    });

    const filteredCollections = collections.filter(col => {
        if (!collectionSearch.trim()) return true;
        const q = collectionSearch.toLowerCase().trim();
        return (
            col.title?.toLowerCase().includes(q) ||
            col.description?.toLowerCase().includes(q) ||
            col.tags?.some(tag => tag.toLowerCase().includes(q))
        );
    });

    // ============================================================
    // Рендер
    // ============================================================
    if (loading) {
        return <div className="collection-loading">Загрузка...</div>;
    }

    if (error) {
        return <div className="collection-error">{error}</div>;
    }

    return (
        <div className="collection-board">
            <div className="collection-header">
                <div className="collection-title-group">
                    <h2>🎮 {channel.name}</h2>
                    <span className="collection-count-badge">
                        {collectionSearch.trim()
                            ? `${filteredCollections.length} / ${collections.length}`
                            : collections.length}
                    </span>
                </div>

                {/* Кнопки — только если canEdit */}
                
                    <div className="collection-header-actions">
                        {!isCollectionSearchOpen ? (
                            <button
                                className="search-icon-btn"
                                onClick={() => setIsCollectionSearchOpen(true)}
                                title="Поиск по коллекции"
                            >
                                🔍
                            </button>
                        ) : (
                            <div className="collection-search-bar">
                                <input
                                    type="text"
                                    value={collectionSearch}
                                    onChange={(e) => setCollectionSearch(e.target.value)}
                                    placeholder="🔍 Поиск..."
                                    className="collection-search-input"
                                    autoFocus
                                />
                                {collectionSearch && (
                                    <button
                                        className="search-clear-btn"
                                        onClick={() => setCollectionSearch('')}
                                    >
                                        ✕
                                    </button>
                                )}
                                <button
                                    className="search-close-btn"
                                    onClick={() => {
                                        setIsCollectionSearchOpen(false);
                                        setCollectionSearch('');
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                        {canEdit && (
                        <button
                            className="add-btn"
                            onClick={() => {
                                setSelectedItemIds([]);
                                setSearchQuery('');
                                setShowModal(true);
                            }}
                        >
                            + Добавить
                        </button>
                        )}
                    </div>
                
            </div>

            <div className="collection-grid">
                {filteredCollections.length === 0 ? (
                    <div className="empty-collection-msg">
                        {collections.length === 0
                            ? 'В коллекции пока нет элементов'
                            : 'Ничего не найдено по запросу'}
                    </div>
                ) : (
                    filteredCollections.map(col => (
                        <div key={col.id} className="collection-card">
                            <div className="collection-card-img">
                                {col.imgUrl
                                    ? <img src={col.imgUrl} alt={col.title} />
                                    : <div className="placeholder">🎮</div>}

                                {canEdit && (
                                    <div className="collection-card-actions">
                                        <button
                                            className="card-action-btn delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(col.id);
                                            }}
                                            title="Удалить"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="collection-card-body">
                                <h3>{col.title}</h3>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Модальное окно — только если canEdit */}
            {showModal && canEdit && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Выберите элементы из набора</h3>

                        <div className="select-search">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="🔍 Поиск..."
                                className="select-search-input"
                                autoFocus
                            />
                            {searchQuery && (
                                <button
                                    className="select-search-clear"
                                    onClick={() => setSearchQuery('')}
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <div className="select-items">
                            {filteredItems.length === 0 ? (
                                <p className="empty-msg">
                                    {setItems.length === 0
                                        ? 'Все элементы уже добавлены'
                                        : 'Ничего не найдено'}
                                </p>
                            ) : (
                                filteredItems.map(item => (
                                    <div
                                        key={item.id}
                                        className={`select-item ${selectedItemIds.includes(item.id) ? 'selected' : ''}`}
                                        onClick={() => toggleItemSelection(item.id)}
                                    >
                                        <div className="select-item-img">
                                            {item.imgUrl
                                                ? <img src={item.imgUrl} alt={item.title} />
                                                : <div className="placeholder">📦</div>}
                                        </div>
                                        <div className="select-item-info">
                                            <h4>{item.title}</h4>
                                            <p>{item.description}</p>
                                        </div>
                                        {selectedItemIds.includes(item.id) && (
                                            <div className="selected-badge">✓</div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="modal-actions">
                            <button onClick={() => setShowModal(false)}>Отмена</button>
                            <button
                                onClick={handleAddSelected}
                                disabled={selectedItemIds.length === 0}
                                className={selectedItemIds.length === 0 ? 'disabled' : ''}
                            >
                                Добавить выбранное ({selectedItemIds.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollectionBoard;